import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Link, useNavigate } from 'react-router-dom';
import { Terminal, Package, Server, ArrowRight, Loader2, Info, Link2, Sparkles, Check, FileText } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LoginArea } from '@/components/auth/LoginArea';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { SKILL_KIND, MCP_KIND, createSkillTags, type SkillType } from '@/lib/skills';

interface SkillFormData {
  identifier: string;
  name: string;
  description: string;
  version: string;
  repository: string;
  homepage: string;
  license: string;
  price: string;
  installCommand: string;
  tags: string;
}

const initialFormData: SkillFormData = {
  identifier: '',
  name: '',
  description: '',
  version: '1.0.0',
  repository: '',
  homepage: '',
  license: 'MIT',
  price: '0',
  installCommand: '',
  tags: '',
};

// Parse GitHub URL to extract owner and repo
function parseGitHubUrl(url: string): { owner: string; repo: string } | null {
  const patterns = [
    /github\.com\/([^\/]+)\/([^\/]+)/,
    /github\.com:([^\/]+)\/([^\/]+)/,
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      return { owner: match[1], repo: match[2].replace(/\.git$/, '') };
    }
  }
  return null;
}

// Fetch GitHub repo metadata + README in parallel
async function fetchGitHubMetadata(url: string): Promise<Partial<SkillFormData> | null> {
  const parsed = parseGitHubUrl(url);
  if (!parsed) return null;

  const base = `https://api.github.com/repos/${parsed.owner}/${parsed.repo}`;

  try {
    const [repoRes, readmeRes] = await Promise.all([
      fetch(base),
      fetch(`${base}/readme`),
    ]);

    if (!repoRes.ok) return null;

    const data = await repoRes.json();

    // Decode README markdown (base64) if available
    let installCommand = '';
    if (readmeRes.ok) {
      const readmeData = await readmeRes.json();
      if (readmeData.content) {
        installCommand = atob(readmeData.content.replace(/\n/g, ''));
      }
    }

    return {
      name: data.name || '',
      description: data.description || '',
      repository: data.html_url || url,
      homepage: data.homepage || '',
      license: data.license?.spdx_id || '',
      tags: (data.topics || []).join(', '),
      identifier: data.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || '',
      installCommand,
    };
  } catch {
    return null;
  }
}

// Parse npm package URL
function parseNpmUrl(url: string): string | null {
  const match = url.match(/npmjs\.com\/package\/([^\/]+)/);
  return match ? match[1] : null;
}

// Fetch npm package metadata
async function fetchNpmMetadata(url: string): Promise<Partial<SkillFormData> | null> {
  const packageName = parseNpmUrl(url);
  if (!packageName) return null;
  
  try {
    const response = await fetch(`https://registry.npmjs.org/${packageName}`);
    if (!response.ok) return null;
    
    const data = await response.json();
    const latest = data['dist-tags']?.latest;
    const latestVersion = latest ? data.versions?.[latest] : null;
    
    return {
      name: data.name || '',
      description: data.description || '',
      repository: typeof data.repository === 'string' ? data.repository : data.repository?.url?.replace(/^git\+/, '').replace(/\.git$/, '') || '',
      homepage: data.homepage || '',
      license: data.license || '',
      version: latest || '1.0.0',
      tags: (data.keywords || []).join(', '),
      identifier: data.name?.replace(/^@[^\/]+\//, '').toLowerCase().replace(/[^a-z0-9]+/g, '-') || '',
    };
  } catch {
    return null;
  }
}

export default function Publish() {
  useSeoMeta({
    title: 'Publish - SkillForge',
    description: 'Publish your AI agent skills and MCP servers to the open marketplace.',
  });

  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { mutateAsync: createEvent, isPending } = useNostrPublish();
  const { toast } = useToast();

  const [type, setType] = useState<SkillType>('skill');
  const [formData, setFormData] = useState<SkillFormData>(initialFormData);
  const [importUrl, setImportUrl] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importSuccess, setImportSuccess] = useState(false);

  const handleChange = (field: keyof SkillFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const generateIdentifier = () => {
    if (formData.name) {
      const slug = formData.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setFormData((prev) => ({ ...prev, identifier: slug }));
    }
  };

  const handleImportUrl = async () => {
    if (!importUrl.trim()) return;
    
    setIsImporting(true);
    setImportSuccess(false);
    
    try {
      let metadata: Partial<SkillFormData> | null = null;
      
      if (importUrl.includes('github.com')) {
        metadata = await fetchGitHubMetadata(importUrl);
      } else if (importUrl.includes('npmjs.com')) {
        metadata = await fetchNpmMetadata(importUrl);
      }
      
      if (metadata) {
        setFormData((prev) => ({
          ...prev,
          ...metadata,
        }));
        setImportSuccess(true);
        toast({
          title: 'Metadata imported!',
          description: 'Fields have been populated from the URL. Review and adjust as needed.',
        });
      } else {
        toast({
          title: 'Could not fetch metadata',
          description: 'Please enter details manually or try a different URL.',
          variant: 'destructive',
        });
      }
    } catch {
      toast({
        title: 'Import failed',
        description: 'Could not fetch metadata from the URL.',
        variant: 'destructive',
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      toast({
        title: 'Not logged in',
        description: 'Please log in with Nostr to publish.',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.identifier || !formData.name || !formData.description) {
      toast({
        title: 'Missing fields',
        description: 'Please fill in all required fields.',
        variant: 'destructive',
      });
      return;
    }

    const price = parseInt(formData.price, 10) || 0;
    const tags = formData.tags
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    const skillTags = createSkillTags({
      identifier: formData.identifier,
      name: formData.name,
      description: formData.description,
      version: formData.version,
      type,
      repository: formData.repository || undefined,
      homepage: formData.homepage || undefined,
      license: formData.license || undefined,
      price,
      installCommand: formData.installCommand || undefined,
      tags,
    });

    try {
      await createEvent({
        kind: type === 'skill' ? SKILL_KIND : MCP_KIND,
        content: '',
        tags: skillTags,
      });

      toast({
        title: 'Published!',
        description: `Your ${type === 'skill' ? 'skill' : 'MCP server'} has been published to Nostr.`,
      });

      navigate(`/skill/${user.pubkey}/${formData.identifier}`);
    } catch (error) {
      console.error('Failed to publish:', error);
      toast({
        title: 'Failed to publish',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <PublishHeader />
        <main className="container mx-auto px-4 py-20">
          <div className="max-w-md mx-auto text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500/20 to-emerald-500/20 flex items-center justify-center mx-auto mb-6 glow-primary border border-white/[0.08]">
              <Terminal className="w-8 h-8 text-orange-400" />
            </div>
            <h1 className="text-2xl font-bold mb-4">Sign in to publish</h1>
            <p className="text-muted-foreground mb-8">
              Connect your Nostr identity to start publishing skills and MCP servers.
            </p>
            <LoginArea />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PublishHeader />

      <main className="container mx-auto px-4 py-10">
        <div className="max-w-2xl mx-auto">
          {/* Page Header */}
          <div className="mb-10">
            <h1 className="text-4xl font-bold mb-3">Publish</h1>
            <p className="text-muted-foreground text-lg">
              Share your AI agent skill or MCP server with the world.
            </p>
          </div>

          {/* Quick Import */}
          <Card className="glass-card mb-8">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Sparkles className="w-5 h-5 text-orange-400" />
                Quick Import
              </CardTitle>
              <CardDescription>
                Paste a GitHub or npm URL to auto-fill the form
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Link2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="https://github.com/user/repo or https://npmjs.com/package/name"
                    value={importUrl}
                    onChange={(e) => setImportUrl(e.target.value)}
                    className="pl-10 h-11 bg-white/[0.02] border-white/[0.08] focus:border-orange-500/50"
                    onKeyDown={(e) => e.key === 'Enter' && handleImportUrl()}
                  />
                </div>
                <Button 
                  onClick={handleImportUrl} 
                  disabled={isImporting || !importUrl.trim()}
                  className="px-6 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 border-0"
                >
                  {isImporting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : importSuccess ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    'Import'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Type Selection */}
          <Tabs value={type} onValueChange={(v) => setType(v as SkillType)} className="mb-8">
            <TabsList className="grid w-full grid-cols-2 bg-white/[0.03] border border-white/[0.08] h-12">
              <TabsTrigger value="skill" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500/80 data-[state=active]:to-amber-500/80">
                <Package className="w-4 h-4" />
                OpenClaw Skill
              </TabsTrigger>
              <TabsTrigger value="mcp" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500/80 data-[state=active]:to-amber-500/80">
                <Server className="w-4 h-4" />
                MCP Server
              </TabsTrigger>
            </TabsList>

            <TabsContent value="skill" className="mt-6">
              <Card className="glass-card border-orange-500/20">
                <CardContent className="pt-4 text-sm">
                  <p className="text-muted-foreground">
                    <strong className="text-foreground">OpenClaw Skills</strong> are capabilities that AI agents can discover, install, and use autonomously. Perfect for CLI tools, APIs, and automation scripts.
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="mcp" className="mt-6">
              <Card className="glass-card border-emerald-500/20">
                <CardContent className="pt-4 text-sm">
                  <p className="text-muted-foreground">
                    <strong className="text-foreground">MCP Servers</strong> implement the Model Context Protocol, allowing AI assistants to interact with external tools and data sources.
                  </p>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>Basic Info</CardTitle>
                <CardDescription>Required information about your {type === 'skill' ? 'skill' : 'MCP server'}.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      placeholder="Flight Search"
                      value={formData.name}
                      onChange={handleChange('name')}
                      onBlur={generateIdentifier}
                      className="h-11 bg-white/[0.02] border-white/[0.08] focus:border-orange-500/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="identifier">Identifier *</Label>
                    <Input
                      id="identifier"
                      placeholder="flight-search"
                      value={formData.identifier}
                      onChange={handleChange('identifier')}
                      className="font-mono h-11 bg-white/[0.02] border-white/[0.08] focus:border-orange-500/50"
                    />
                    <p className="text-xs text-muted-foreground">Unique slug (auto-generated from name)</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    placeholder="Search and compare flight prices from multiple airlines..."
                    value={formData.description}
                    onChange={handleChange('description')}
                    rows={3}
                    className="bg-white/[0.02] border-white/[0.08] focus:border-orange-500/50"
                  />
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <FileText className="w-3 h-3 text-orange-400/70" />
                    Supports <strong className="text-orange-400/90 font-medium">Markdown</strong> — headings, bold, lists, links, and code blocks
                  </p>
                </div>

                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="version">Version</Label>
                    <Input
                      id="version"
                      placeholder="1.0.0"
                      value={formData.version}
                      onChange={handleChange('version')}
                      className="font-mono h-11 bg-white/[0.02] border-white/[0.08] focus:border-orange-500/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="license">License</Label>
                    <Input
                      id="license"
                      placeholder="MIT"
                      value={formData.license}
                      onChange={handleChange('license')}
                      className="h-11 bg-white/[0.02] border-white/[0.08] focus:border-orange-500/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tags">Tags</Label>
                    <Input
                      id="tags"
                      placeholder="travel, flights, api"
                      value={formData.tags}
                      onChange={handleChange('tags')}
                      className="h-11 bg-white/[0.02] border-white/[0.08] focus:border-orange-500/50"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Links */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>Links</CardTitle>
                <CardDescription>Repository and documentation links.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="repository">Repository URL</Label>
                    <Input
                      id="repository"
                      placeholder="https://github.com/username/project"
                      value={formData.repository}
                      onChange={handleChange('repository')}
                      className="h-11 bg-white/[0.02] border-white/[0.08] focus:border-orange-500/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="homepage">Homepage</Label>
                    <Input
                      id="homepage"
                      placeholder="https://your-project.dev"
                      value={formData.homepage}
                      onChange={handleChange('homepage')}
                      className="h-11 bg-white/[0.02] border-white/[0.08] focus:border-orange-500/50"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pricing (Advanced) */}
            <Card className="glass-card border-dashed">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  Pricing
                  <span className="text-xs font-normal text-muted-foreground bg-white/[0.05] px-2 py-0.5 rounded">Advanced</span>
                </CardTitle>
                <CardDescription>
                  Most skills are free. Paid skills require you to run a DVM server.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="price">Price (sats)</Label>
                  <Input
                    id="price"
                    type="number"
                    min="0"
                    placeholder="0"
                    value={formData.price}
                    onChange={handleChange('price')}
                    className="h-11 bg-white/[0.02] border-white/[0.08] focus:border-orange-500/50"
                  />
                  <p className="text-xs text-muted-foreground">Leave at 0 for free (recommended)</p>
                </div>

                {parseInt(formData.price) > 0 && (
                  <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-amber-400 mb-1">DVM Server Required</p>
                        <p className="text-muted-foreground">
                          Paid skills require you to run a NIP-90 Data Vending Machine server to handle payments and deliver results. 
                          <a href="/nip.md" target="_blank" className="text-amber-400 hover:underline ml-1">Learn more</a>
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Installation (Optional - Collapsed) */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>Installation Instructions</CardTitle>
                <CardDescription>Optional. Leave blank to auto-generate based on type.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <Textarea
                  id="installCommand"
                  placeholder={type === 'mcp' ? '# Add to your MCP config:\n{\n  "mcpServers": {\n    "your-server": { ... }\n  }\n}' : '# Install via npm\nnpm install your-package'}
                  value={formData.installCommand}
                  onChange={handleChange('installCommand')}
                  rows={8}
                  className="font-mono text-sm bg-white/[0.02] border-white/[0.08] focus:border-orange-500/50"
                />
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <FileText className="w-3 h-3 text-orange-400/70" />
                  Supports <strong className="text-orange-400/90 font-medium">Markdown</strong> — paste a full README or write step-by-step instructions
                </p>
              </CardContent>
            </Card>

            {/* Submit */}
            <div className="flex justify-end gap-4 pt-4">
              <Link to="/browse">
                <Button type="button" variant="ghost" className="h-11 px-6">Cancel</Button>
              </Link>
              <Button type="submit" disabled={isPending} className="gap-2 px-8 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 border-0 shadow-lg shadow-orange-500/25">
                {isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Publishing...
                  </>
                ) : (
                  <>
                    Publish to Nostr
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}

function PublishHeader() {
  return (
    <header className="sticky top-0 z-50 backdrop-blur-2xl bg-background/60 border-b border-white/[0.06]">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500/20 to-emerald-500/20 flex items-center justify-center glow-primary-sm border border-white/[0.08] group-hover:border-white/[0.15] transition-all">
            <Terminal className="w-4 h-4 text-orange-400" />
          </div>
          <span className="font-semibold text-lg tracking-tight">SkillForge</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
          <Link to="/browse" className="hover:text-foreground transition-colors">Browse</Link>
          <Link to="/connect-agent" className="hover:text-foreground transition-colors">Connect Agent</Link>
          <Link to="/publish" className="text-foreground font-medium opacity-50 text-xs">Publish</Link>
        </nav>

        <LoginArea className="max-w-60" />
      </div>
    </header>
  );
}
